import { Component, OnInit, Output, EventEmitter, Input } from '@angular/core';
import { FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { ADPResourceService, CustomFormControl, ListItem } from 'digital-core';
import { AccountService } from 'src/app/core/services/account.service';
import { GoogleAnalyticsService } from 'src/app/core/services/google-tag-manager.service';
import { ContractContactDetails, OtpChannel, SendOTPRequest } from 'src/app/model/account/user-otp-request';
import { GTMOnboardingEvents, OnboardingSteps } from 'src/app/model/google-tag-manager/api-status';
import { GlobalModalWindowService } from '../../../../../core/services/global-modal-window.service';

@Component({
  selector: 'app-security-verification',
  templateUrl: './security-verification.component.html',
  styleUrls: ['./security-verification.component.scss'],
})
export class SecurityVerificationComponent implements OnInit {
  verificationOptionForm: FormGroup;
  showEmailOption = false;
  showPhoneOption = false;
  phoneNumbers: ListItem[] = [];
  emails: ListItem[] = [];
  primaryEmail: string;
  primaryPhone: string;
  verificationOptions: ListItem[] = [];
  selectedOptionDisplayText: string;
  showRatesAndTermsSectionBelow = false;
  showVerificationOptionValueInAgreeOrDisagree = true;
  isSPOEEnabled: boolean = false;

  @Input() lookupResponse: any;
  @Input() isReviewContactFinished: boolean;
  @Input() isSecondaryOTP: boolean;
  @Input() isSecondaryOTPNeeded: boolean;
  @Input() contactInfo: ContractContactDetails;
  @Output() createOTPResponse = new EventEmitter();

  constructor(
    private router: Router,
    public resourceService: ADPResourceService,
    private googleAnalyticsService: GoogleAnalyticsService,
    private accountService: AccountService,
    private globalModalWindowService: GlobalModalWindowService
  ) {}

  ngOnInit() {
    if (this.lookupResponse) {
      this.initForm();
      this.initListItems();
      this.initVerificationOptions();
    }
    this.showRatesAndTermsSectionBelow = this.resourceService.isPortalSettingTrue('LookupUser_Verification_Rates_And_Terms_Section');
    this.showVerificationOptionValueInAgreeOrDisagree = this.resourceService.isPortalSettingTrue('LookupUser_Verification_Option_Value_In_Agree_Or_Disagree');
    this.isSPOEEnabled = this.resourceService.isPortalSettingTrue('Global_SPOE_Enabled');

    !this.isReviewContactFinished
      ? this.googleAnalyticsService.emitOnboardingEvent(GTMOnboardingEvents.InProcess, OnboardingSteps.VerifyAccount)
      : this.googleAnalyticsService.emitOnboardingEvent(GTMOnboardingEvents.InProcess, OnboardingSteps.VerifyNewEmail);
  }

  initListItems() {
    this.emails = [];
    this.lookupResponse.emails.map(x => {
      this.emails.push(new ListItem('',
        x.substring(x.indexOf(':')+1), //Encrypted value of email
        x.substring(0, x.indexOf(':')) //Masked value of email
      ));
    });

    this.phoneNumbers = [];
    this.lookupResponse.phoneNumbers.map(x => {
      this.phoneNumbers.push(new ListItem('',
        x.substring(x.indexOf(':')+1), //Encrypted value of phone
        x.substring(0, x.indexOf(':')) //Masked value of phone
      ));
    });
  }

  initForm() {
    this.verificationOptionForm = new FormGroup({
      VerificationOption: new CustomFormControl('', Validators.required)
    });
  }

  initVerificationOptions() {
    let availableOptionsList: ListItem[] = [];
    this.resourceService.getListItemResource("Global_Security_Verification_Options").subscribe((options) => {
      availableOptionsList = options;
    });

    availableOptionsList?.forEach(option => {
      switch (option.Key) {
        case OtpChannel[OtpChannel.sms]:
          if (this.lookupResponse?.phoneNumbers?.length > 0 && this.resourceService.isPortalSettingTrue('LookupUser_VerificationOption_TextMessage')) {
            option.Value += this.lookupResponse?.phoneNumbers[0].substring(0, this.lookupResponse?.phoneNumbers[0].indexOf(':'));
            this.verificationOptions.push(option);
          }
          break;
        case OtpChannel[OtpChannel.email]:
          if (this.lookupResponse?.emails?.length > 0 && this.resourceService.isPortalSettingTrue('LookupUser_VerificationOption_Email')) {
            option.Value += this.lookupResponse?.emails[0].substring(0, this.lookupResponse?.emails[0].indexOf(':'));
            this.verificationOptions.push(option);
          }
          break;
        case OtpChannel[OtpChannel.call]:
          if (this.lookupResponse?.phoneNumbers?.length > 0 && this.resourceService.isPortalSettingTrue('LookupUser_VerificationOption_VoiceCall')) {
            this.verificationOptions.push(option);
          }
          break;
      }
    });
  }

  onOptionChanged(option: ListItem) {
    if (this.showVerificationOptionValueInAgreeOrDisagree) {
      this.selectedOptionDisplayText = option.Value;
    }
    switch (option.Key) {
      case OtpChannel[OtpChannel.sms]:
        this.showPhoneOption = true;
        this.showEmailOption = false;
        break;
      case OtpChannel[OtpChannel.call]:
        this.showPhoneOption = true;
        this.showEmailOption = false;
        if (this.showVerificationOptionValueInAgreeOrDisagree) {
          this.selectedOptionDisplayText = this.resourceService.getViewLabel('LookupUser_VerificationOption_Text_VoiceCall');
        }
        break;
      case OtpChannel[OtpChannel.email]:
        this.showPhoneOption = false;
        this.showEmailOption = true;
        break;
    }
  }

  continue() {
    let request = new SendOTPRequest();
    if (!this.isReviewContactFinished && !this.isSecondaryOTPNeeded) {
      request.channel = this.verificationOptionForm.get('VerificationOption').value?.Key;

      switch (request.channel) {
        case OtpChannel[OtpChannel.email]:
          request.userContact = this.emails[0]?.Key; // Encrypted value of email
          request.maskedUserContact = this.emails[0]?.Value; // Masked value of email
          break;
        case OtpChannel[OtpChannel.sms]:
        case OtpChannel[OtpChannel.call]:
          request.userContact = this.phoneNumbers[0]?.Key; // Encrypted value of phone
          request.maskedUserContact = this.phoneNumbers[0]?.Value; // Masked value of phone
          break;
      }
    } else {
      request.channel = OtpChannel[OtpChannel.email];
      request.userContact = this.contactInfo.primaryEmail; // This can be 'phone' if OTP needs to be vefied by phone number
      request.maskedUserContact = undefined;
    }

    this.accountService.sendOTPCode(request).subscribe({
      next: (resp) => {
        !this.isReviewContactFinished
          ? this.googleAnalyticsService.emitOnboardingEvent(GTMOnboardingEvents.Complete, OnboardingSteps.VerifyAccount)
          : this.googleAnalyticsService.emitOnboardingEvent(GTMOnboardingEvents.Complete, OnboardingSteps.VerifyNewEmail);
        // Note: To get the count of send attempts use "resp.sendAttempts";
        // Emitting the same request is intentional to reuse for the next component
        this.createOTPResponse.emit(request);
      },
      error: (err) => {
        const emitError = {
          message: err?.error?.Message,
          type: err?.error?.ExceptionType,
          code: err?.error?.ErrorCode,
        }
        !this.isReviewContactFinished
          ? this.googleAnalyticsService.emitOnboardingEvent(GTMOnboardingEvents.Failed, OnboardingSteps.VerifyAccount, emitError)
          : this.googleAnalyticsService.emitOnboardingEvent(GTMOnboardingEvents.Failed, OnboardingSteps.VerifyNewEmail, emitError);
        if (err.error && err.error.ErrorCode == 'CreateOTP') {
          this.router.navigate(['/tech-support'], { queryParams: { headerKey: 'TechSupport_Signup_Create_Account_CreateOTP' }});
        } else if (err.error && err.error.ErrorCode == 'SMSNotSupportedOnLandline') {
          this.router.navigate(['/tech-support'], { queryParams: { headerKey: 'TechSupport_Signup_Create_Account_SMSNotSupportedOnLandline' }});
        } else if (err.error && err.error.ErrorCode == 'InvalidPhoneNumber') {
          this.router.navigate(['/tech-support'], { queryParams: { headerKey: 'Global_Error_Code_InvalidPhoneNumber' }});
        } else if (err.error && err.error.ErrorCode == 'OTPSendAttemptReached') {
          this.router.navigate(['/tech-support'], { queryParams: { headerKey: 'TechSupport_Signup_Create_Account_OTPSendAttemptReached' }});
        } else {
          this.router.navigate(['/tech-support'], { queryParams: { headerKey: 'TechSupport_HavingTrouble' }});
        }
      }
    });
  }

  getResourceObject(name: string, type: string, namespace = null) {
    namespace = namespace ? namespace : "LookupUser_VerificationOption";
    let title = namespace + "_" + type + "_" + name;
    return this.resourceService.getViewResourceObject(title);
  }

  showSupportInfo() {
    this.globalModalWindowService.openSPOESimpleErrorDialog();
  }
}
